import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Users, Trophy, Plus, UserPlus, Edit, Trash2, Target } from "lucide-react";

const TeamsTab = ({
  teamStats,
  teamLeaderboard,
  challenges,
  selectedChallenge,
  setSelectedChallenge,
  isAuthenticated,
  currentUser,
  setShowTeamDialog,
  setTeamToAddMember,
  setShowAddMemberToTeamDialog,
  setEditingTeam,
  handleDeleteTeam,
  fetchTeamDetails
}) => {
  return (
    <div className="space-y-6">
      {/* Show message if no challenge selected */}
      {selectedChallenge === "overall" ? (
        <Card className="bg-slate-800/80 border-slate-700 backdrop-blur-sm">
          <CardContent className="py-12 text-center">
            <Trophy className="w-16 h-16 mx-auto mb-4 text-amber-400 opacity-50" />
            <h3 className="text-xl font-semibold text-white mb-2">Select a Monthly Challenge</h3>
            <p className="text-slate-400 max-w-md mx-auto">
              Team rankings are based on monthly challenge performance. 
              Please select a specific challenge from the dropdown above to view the league standings.
            </p>
            <div className="mt-6">
              <Select value={selectedChallenge} onValueChange={setSelectedChallenge}>
                <SelectTrigger className="w-64 mx-auto bg-slate-700 border-slate-600 text-white">
                  <SelectValue placeholder="Select Challenge" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {challenges.map(challenge => (
                    <SelectItem key={challenge.id} value={challenge.id}>
                      {challenge.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Challenge Info Banner */}
          <Card className="bg-gradient-to-r from-blue-900/40 to-purple-900/40 border-blue-700/50">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-300 text-sm font-medium">League Rankings For:</p>
                  <p className="text-xl font-bold text-white">
                    {challenges.find(c => c.id === selectedChallenge)?.title || "Selected Challenge"}
                  </p>
                </div>
                <Target className="w-10 h-10 text-blue-400" />
              </div>
            </CardContent>
          </Card>

          {/* Team Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-gradient-to-br from-amber-900/40 to-orange-900/40 border-amber-700/50">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-amber-300 text-sm font-medium">Total Teams</p>
                    <p className="text-3xl font-bold text-amber-100">{teamStats.total_teams || 0}</p>
                  </div>
                  <Users className="w-10 h-10 text-amber-400" />
                </div>
              </CardContent>
            </Card>
            
            {teamStats.top_team && (
              <Card className="bg-gradient-to-br from-yellow-900/40 to-amber-900/40 border-yellow-700/50 md:col-span-2">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-yellow-300 text-sm font-medium">Leading Team</p>
                      <p className="text-2xl font-bold text-yellow-100">{teamStats.top_team.name}</p>
                      <p className="text-yellow-300/80 text-sm">
                        {teamStats.top_team.total_team_points.toLocaleString()} SP
                        {teamStats.top_team.captain_name && ` | Captain: ${teamStats.top_team.captain_name}`}
                      </p>
                    </div>
                    <Trophy className="w-12 h-12 text-yellow-400" />
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Team Leaderboard Card */}
          <Card className="bg-slate-800/80 border-slate-700 backdrop-blur-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-white flex items-center gap-2">
                  CSH Story Utsav League
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Team rankings based on this month&apos;s submissions
                </CardDescription>
              </div>
              {(isAuthenticated || currentUser?.is_admin) && (
                <Button
                  onClick={() => setShowTeamDialog(true)}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="create-team-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Team
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {teamLeaderboard.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <Users className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <h3 className="text-lg font-medium mb-2 text-white">No Team Activity Yet</h3>
                  <p>Teams haven&apos;t scored points in this challenge yet.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {teamLeaderboard.map((entry, index) => (
                    <div
                      key={entry.team.id}
                      data-testid={`team-leaderboard-entry-${index}`}
                      className={`p-4 rounded-lg border transition-all cursor-pointer hover:scale-[1.01] ${
                        index === 0
                          ? "bg-gradient-to-r from-yellow-900/30 to-amber-900/30 border-yellow-600/50"
                          : index === 1
                          ? "bg-gradient-to-r from-slate-700/30 to-slate-600/30 border-slate-500/50"
                          : index === 2
                          ? "bg-gradient-to-r from-orange-900/30 to-amber-900/30 border-orange-600/50"
                          : "bg-slate-800/50 border-slate-700/50"
                      }`}
                      onClick={() => fetchTeamDetails(entry.team.id, selectedChallenge)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                            index === 0 ? "bg-yellow-500 text-yellow-900" :
                            index === 1 ? "bg-slate-400 text-slate-900" :
                            index === 2 ? "bg-orange-500 text-orange-900" :
                            "bg-slate-700 text-slate-300"
                          }`}>
                            {entry.rank}
                          </div>
                          <div>
                            <h3 className="font-semibold text-white text-lg">{entry.team.name}</h3>
                            <p className="text-slate-400 text-sm">
                              {entry.member_count} players
                              {entry.captain_name && ` | Captain: ${entry.captain_name}`}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-amber-400">
                            {entry.total_team_points.toLocaleString()}
                          </p>
                          <p className="text-slate-400 text-sm">Story Points</p>
                        </div>
                      </div>
                      
                      {/* Admin actions */}
                      {(isAuthenticated || currentUser?.is_admin) && (
                        <div className="flex gap-2 mt-3 pt-3 border-t border-slate-700/50" onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setTeamToAddMember(entry.team);
                              setShowAddMemberToTeamDialog(true);
                            }}
                            className="text-green-400 border-green-600 hover:bg-green-900/30"
                          >
                            <UserPlus className="w-3 h-3 mr-1" />
                            Add Player
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingTeam(entry.team)}
                            className="text-blue-400 border-blue-600 hover:bg-blue-900/30"
                          >
                            <Edit className="w-3 h-3 mr-1" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteTeam(entry.team.id, entry.team.name)}
                            className="text-red-400 border-red-600 hover:bg-red-900/30"
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Delete
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default TeamsTab;
